import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, CalendarCheck, CreditCard, Loader2, PenLine, Save } from 'lucide-react';
import type { CampingSpot, AppUser } from '../../types';
import { updateCampsiteStatus } from '../../services/firebaseService';

interface Props {
  spot: CampingSpot;
  currentUser: AppUser | null;
}

const CAPACITY_OPTIONS = [
  { value: 'available', labelKey: 'campsite.capacity_available', fallback: 'Ledige plasser', color: 'emerald' },
  { value: 'limited',   labelKey: 'campsite.capacity_limited',   fallback: 'Få plasser igjen', color: 'amber' },
  { value: 'full',      labelKey: 'campsite.capacity_full',      fallback: 'Fullt',            color: 'red' },
] as const;

const SEASON_OPTIONS = [
  { value: 'open',   labelKey: 'campsite.season_open',   fallback: 'Åpen nå',   color: 'emerald' },
  { value: 'closed', labelKey: 'campsite.season_closed', fallback: 'Stengt',    color: 'red' },
] as const;

function relativeTime(ms: number, t: (k: string, opts?: any) => string): string {
  const minutes = Math.floor((Date.now() - ms) / 60000);
  if (minutes < 60) return t('campsite.time_minutes_ago', { n: minutes, defaultValue: `${minutes} min siden` });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('campsite.time_hours_ago', { n: hours, defaultValue: `${hours} t siden` });
  const days = Math.floor(hours / 24);
  return t('campsite.time_days_ago', { n: days, defaultValue: `${days} d siden` });
}

const CommercialCampsiteBlock: React.FC<Props> = ({ spot, currentUser }) => {
  const { t } = useTranslation();
  // Local overlay for optimistic updates — no parent callback needed
  const [localMeta, setLocalMeta] = useState(spot.commercialMetadata);
  const meta = localMeta;
  const [savingCapacity, setSavingCapacity] = useState(false);
  const [savingSeason, setSavingSeason] = useState(false);
  const [savingFee, setSavingFee] = useState(false);
  const [showCapacityPicker, setShowCapacityPicker] = useState(false);
  const [showSeasonPicker, setShowSeasonPicker] = useState(false);
  const [editingFee, setEditingFee] = useState(false);
  const [feeInput, setFeeInput] = useState('');

  if (spot.spotType !== 'commercial_campsite') return null;

  const capacityColor = meta?.capacityStatus === 'full' ? 'red'
    : meta?.capacityStatus === 'limited' ? 'amber'
    : meta?.capacityStatus === 'available' ? 'emerald' : 'stone';

  const seasonColor = meta?.seasonStatus === 'open' ? 'emerald'
    : meta?.seasonStatus === 'closed' ? 'red' : 'stone';

  const handleCapacity = async (value: 'full' | 'limited' | 'available') => {
    if (!currentUser || savingCapacity) return;
    setSavingCapacity(true);
    setShowCapacityPicker(false);
    try {
      const now = Date.now();
      await updateCampsiteStatus(spot.id, { capacityStatus: value, capacityReportedAt: now, capacityReportedBy: currentUser.uid });
      setLocalMeta(prev => ({ ...prev, capacityStatus: value, capacityReportedAt: now, capacityReportedBy: currentUser.uid }));
    } catch (e) { console.error('updateCampsiteStatus capacity error', e); }
    finally { setSavingCapacity(false); }
  };

  const handleSeason = async (value: 'open' | 'closed') => {
    if (!currentUser || savingSeason) return;
    setSavingSeason(true);
    setShowSeasonPicker(false);
    try {
      const now = Date.now();
      await updateCampsiteStatus(spot.id, { seasonStatus: value, seasonUpdatedAt: now, seasonUpdatedBy: currentUser.uid });
      setLocalMeta(prev => ({ ...prev, seasonStatus: value, seasonUpdatedAt: now, seasonUpdatedBy: currentUser.uid }));
    } catch (e) { console.error('updateCampsiteStatus season error', e); }
    finally { setSavingSeason(false); }
  };

  const handleFee = async () => {
    if (!currentUser || savingFee) return;
    const value = feeInput.trim();
    setSavingFee(true);
    try {
      await updateCampsiteStatus(spot.id, { fee: value });
      setLocalMeta(prev => ({ ...prev, fee: value }));
      setEditingFee(false);
    } catch (e) { console.error('updateCampsiteStatus fee error', e); }
    finally { setSavingFee(false); }
  };

  // Kontaktinfo (nettside/telefon/åpningstider/pris) er flyttet til den delte
  // ContactInfoBlock i OVERSIKT. Denne blokken eier nå kun den interaktive
  // sesong-/kapasitet-rapporteringen, som hører hjemme under FASILITETER.
  const hasAnyMeta = meta?.capacityStatus || meta?.seasonStatus || currentUser;

  if (!hasAnyMeta) return null;

  return (
    <div className="bg-[var(--bg-surface-alt)] rounded-2xl border border-[var(--border-primary)] p-4 space-y-3">
      {/* Pris (redigerbart av innloggede — eier/bruker; overstyrer Google-prisnivå) */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <CreditCard size={13} className="text-stone-400" />
            <span className="text-tiny font-bold text-[var(--text-secondary)]">
              {t('campsite.price_label', 'Pris')}
            </span>
            {meta?.fee && meta.fee.trim() && !editingFee && (
              <span className="px-2 py-0.5 rounded-full text-3xs font-black uppercase tracking-wider bg-stone-500/15 text-[var(--text-secondary)]">
                {meta.fee}
              </span>
            )}
          </div>
          {currentUser && !editingFee && (
            <button
              onClick={e => { e.stopPropagation(); setFeeInput(meta?.fee || ''); setEditingFee(true); setShowSeasonPicker(false); setShowCapacityPicker(false); }}
              className="flex items-center gap-1 text-3xs font-black uppercase tracking-wider text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
            >
              <PenLine size={11} /> {meta?.fee ? t('campsite.edit', 'Rediger') : t('campsite.report', 'Rapporter')}
            </button>
          )}
        </div>
        {editingFee && (
          <div className="flex gap-2 pl-5" onClick={e => e.stopPropagation()}>
            <input
              type="text"
              value={feeInput}
              onChange={e => setFeeInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleFee(); if (e.key === 'Escape') setEditingFee(false); }}
              placeholder={t('campsite.price_placeholder', 'F.eks. 350 kr/natt')}
              className="flex-1 bg-black/20 border border-[var(--border-primary)] rounded-lg px-2.5 py-1.5 text-2xs text-[var(--text-primary)] placeholder-stone-600 focus:outline-none focus:border-emerald-500/50"
            />
            <button
              onClick={e => { e.stopPropagation(); handleFee(); }}
              disabled={savingFee}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-2xs font-black bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25 transition-colors"
            >
              {savingFee ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
            </button>
          </div>
        )}
      </div>

      {/* Sesongstatus */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <CalendarCheck size={13} className={`text-${seasonColor}-400`} />
            <span className="text-tiny font-bold text-[var(--text-secondary)]">
              {t('campsite.season_label', 'Sesong')}
            </span>
            {meta?.seasonStatus && meta.seasonStatus !== 'unknown' && (
              <span className={`px-2 py-0.5 rounded-full text-3xs font-black uppercase tracking-wider bg-${seasonColor}-500/15 text-${seasonColor}-400`}>
                {t(`campsite.season_${meta.seasonStatus}`, meta.seasonStatus === 'open' ? 'Åpen' : 'Stengt')}
              </span>
            )}
            {meta?.seasonUpdatedAt && (
              <span className="text-3xs text-[var(--text-muted)]">
                {relativeTime(meta.seasonUpdatedAt, t)}
              </span>
            )}
          </div>
          {currentUser && (
            <button
              onClick={e => { e.stopPropagation(); setShowSeasonPicker(p => !p); setShowCapacityPicker(false); }}
              disabled={savingSeason}
              className="flex items-center gap-1 text-3xs font-black uppercase tracking-wider text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
            >
              {savingSeason ? <Loader2 size={11} className="animate-spin" /> : t('campsite.report', 'Rapporter')}
            </button>
          )}
        </div>
        {showSeasonPicker && (
          <div className="flex gap-2 pl-5">
            {SEASON_OPTIONS.map(o => (
              <button
                key={o.value}
                onClick={e => { e.stopPropagation(); handleSeason(o.value); }}
                className={`px-3 py-1.5 rounded-xl text-2xs font-black uppercase tracking-wider bg-${o.color}-500/15 text-${o.color}-400 hover:bg-${o.color}-500/25 transition-colors`}
              >
                {t(o.labelKey, o.fallback)}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Kapasitetsstatus */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Users size={13} className={`text-${capacityColor}-400`} />
            <span className="text-tiny font-bold text-[var(--text-secondary)]">
              {t('campsite.capacity_label', 'Kapasitet')}
            </span>
            {meta?.capacityStatus && meta.capacityStatus !== 'unknown' && (
              <span className={`px-2 py-0.5 rounded-full text-3xs font-black uppercase tracking-wider bg-${capacityColor}-500/15 text-${capacityColor}-400`}>
                {t(`campsite.capacity_${meta.capacityStatus}`,
                  meta.capacityStatus === 'full' ? 'Fullt' :
                  meta.capacityStatus === 'limited' ? 'Få plasser' : 'Ledige plasser')}
              </span>
            )}
            {meta?.capacityReportedAt && (
              <span className="text-3xs text-[var(--text-muted)]">
                {relativeTime(meta.capacityReportedAt, t)}
              </span>
            )}
          </div>
          {currentUser && (
            <button
              onClick={e => { e.stopPropagation(); setShowCapacityPicker(p => !p); setShowSeasonPicker(false); }}
              disabled={savingCapacity}
              className="flex items-center gap-1 text-3xs font-black uppercase tracking-wider text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
            >
              {savingCapacity ? <Loader2 size={11} className="animate-spin" /> : t('campsite.report', 'Rapporter')}
            </button>
          )}
        </div>
        {showCapacityPicker && (
          <div className="flex gap-2 pl-5 flex-wrap">
            {CAPACITY_OPTIONS.map(o => (
              <button
                key={o.value}
                onClick={e => { e.stopPropagation(); handleCapacity(o.value); }}
                className={`px-3 py-1.5 rounded-xl text-2xs font-black uppercase tracking-wider bg-${o.color}-500/15 text-${o.color}-400 hover:bg-${o.color}-500/25 transition-colors`}
              >
                {t(o.labelKey, o.fallback)}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CommercialCampsiteBlock;
